import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const navigate = useNavigate();

  const handleGetQuote = () => {
    setIsMenuOpen(false);
    navigate('/contact#quote-form');
    // Scroll to form after navigation
    setTimeout(() => {
      const formElement = document.getElementById('quote-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus first input for accessibility
        const firstInput = formElement.querySelector('input, textarea, select');
        if (firstInput instanceof HTMLElement) {
          firstInput.focus();
        }
      }
    }, 100);
  };

  useEffect(() => {
    const handleScroll = () => {
      // Shrink logo after scrolling down 60px
      setIsScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Trigger initial spin animation
    const timer = setTimeout(() => {
      setHasSpun(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  
  return (
    <nav 
      className={`bg-white shadow-md sticky top-0 z-50 ${
        isScrolled ? 'py-3' : 'py-4 md:py-6'
      }`}
      style={{
        transition: 'padding 600ms cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}
    >
      <div className="container-custom relative">
        <div className="flex justify-between items-center">
          {/* Logo - Expands on page top, shrinks on scroll */}
          <Link to="/" className="flex items-center relative z-10">
            <div className="relative">
              {/* Logo image with scroll-based sizing. WebP variant is served
                  first, PNG is the fallback for browsers without WebP (effectively
                  none in our target matrix, but the fallback stays for IE-era
                  crawlers and email previews). */}
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img
                  src="/logo.png"
                  alt="Elevation Electrical, LLC - Licensed Electrician"
                  width={128}
                  height={128}
                  className={`mr-3 ${
                    isScrolled
                      ? 'h-12 w-12'
                      : 'h-20 w-20 md:h-28 md:w-28 lg:h-32 lg:w-32'
                  }`}
                  style={{
                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                    transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    animation: !hasSpun ? 'spin-logo 2s ease-in-out forwards' : 'none'
                  }}
                />
              </picture>
            </div>
            <span 
              className={`font-bold text-navy ${
                isScrolled ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl lg:text-4xl'
              }`}
              style={{
                transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              Elevation <span className="text-orange">Electrical</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-navy hover:text-orange font-medium transition-colors">Home</Link>
            <Link to="/about" className="text-navy hover:text-orange font-medium transition-colors">About Us</Link>
            <Link to="/services" className="text-navy hover:text-orange font-medium transition-colors">Services</Link>
            <Link to="/tools" className="text-navy hover:text-orange font-medium transition-colors">Free Tools</Link>
            <Link to="/now-hiring" className="text-navy hover:text-orange font-medium transition-colors">Now Hiring</Link>
            <Link to="/contact" className="text-navy hover:text-orange font-medium transition-colors">Contact</Link>
          </div>
          
          {/* Call Now Button */}
          <div className="hidden md:flex items-center">
            <a href="tel:4079707211" className="flex items-center text-orange font-bold mr-4">
              <Phone size={20} className="mr-2" />
              (407) 970-7211
            </a>
            <Button 
              className="bg-orange hover:bg-orange-dark text-navy"
              onClick={handleGetQuote}
            >
              Get a Quote
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <a
              href="tel:4079707211"
              className="text-orange mr-4"
              aria-label="Call Elevation Electrical at (407) 970-7211"
            >
              <Phone size={24} aria-hidden="true" />
            </a>
            <button
              onClick={toggleMenu}
              className="text-navy"
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? (
                <X size={28} aria-hidden="true" />
              ) : (
                <Menu size={28} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            id="mobile-navigation"
            className="lg:hidden mt-4 py-4 animate-fade-in"
          >
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-navy hover:text-orange font-medium" onClick={toggleMenu}>Home</Link>
              <Link to="/about" className="text-navy hover:text-orange font-medium" onClick={toggleMenu}>About Us</Link>
              <Link to="/services" className="text-navy hover:text-orange font-medium" onClick={toggleMenu}>Services</Link>
              <Link to="/tools" className="text-navy hover:text-orange font-medium" onClick={toggleMenu}>Free Tools</Link>
              <Link to="/now-hiring" className="text-navy hover:text-orange font-medium" onClick={toggleMenu}>Now Hiring</Link>
              <Link to="/contact" className="text-navy hover:text-orange font-medium" onClick={toggleMenu}>Contact</Link>
              <Button 
                className="bg-orange hover:bg-orange-dark text-navy w-full"
                onClick={handleGetQuote}
              >
                Get a Quote
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
